import React from "react";
import "@styles/Dashboard/Dashboard.css";
import Card from "./Card";
import LineGraph from "./LineGraph";
import BarGraph from "./BarGraph";
import OrdersByHour from "./OrdersByHour";
import OrderStatusDonut from "./OrderStatusDonut";
import RevenueByFoodCourt from "./RevenueByFoodCourt";
import AvgOrderValueTrend from "./AvgOrderValueTrend";
import RegistrationFunnel from "./RegistrationFunnel";

const Dashboard = () => {
  const today = new Date();
  const formatted = today.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="dash">
      {/* Header */}
      <div className="dash-header">
        <div>
          <h1 className="dash-title">Dashboard</h1>
          <p className="dash-subtitle">Operational Overview and Performance Metrics</p>
        </div>
        <div className="dash-header-right">
          <span className="dash-date">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            {formatted}
          </span>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="dash-cards">
        <Card />
      </div>

      {/* Row 1: Existing Charts */}
      <div className="dash-section-label">Revenue & Orders</div>
      <div className="dash-charts">
        <div className="dash-chart-card">
          <div className="dash-chart-header">
            <div>
              <h3 className="dash-chart-title">Platform Fee Growth</h3>
              <p className="dash-chart-desc">Daily platform fee trends (7 days)</p>
            </div>
            <div className="dash-chart-badge purple">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
              Live
            </div>
          </div>
          <div className="dash-chart-body"><LineGraph /></div>
        </div>
        <div className="dash-chart-card">
          <div className="dash-chart-header">
            <div>
              <h3 className="dash-chart-title">Order Growth</h3>
              <p className="dash-chart-desc">Daily order volume (7 days)</p>
            </div>
            <div className="dash-chart-badge amber">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/></svg>
              Orders
            </div>
          </div>
          <div className="dash-chart-body"><BarGraph /></div>
        </div>
      </div>

      {/* Row 2: Analytics Charts */}
      <div className="dash-section-label">Analytics</div>
      <div className="dash-charts three-col">
        <div className="dash-chart-card">
          <div className="dash-chart-header">
            <div>
              <h3 className="dash-chart-title">Orders by Hour</h3>
              <p className="dash-chart-desc">Peak ordering hours (30 days)</p>
            </div>
            <div className="dash-chart-badge cyan">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              Hourly
            </div>
          </div>
          <div className="dash-chart-body"><OrdersByHour /></div>
        </div>
        <div className="dash-chart-card">
          <div className="dash-chart-header">
            <div>
              <h3 className="dash-chart-title">Order Status</h3>
              <p className="dash-chart-desc">Status distribution (all time)</p>
            </div>
            <div className="dash-chart-badge green">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              Status
            </div>
          </div>
          <div className="dash-chart-body"><OrderStatusDonut /></div>
        </div>
        <div className="dash-chart-card">
          <div className="dash-chart-header">
            <div>
              <h3 className="dash-chart-title">Revenue by Food Court</h3>
              <p className="dash-chart-desc">Top performing partners</p>
            </div>
            <div className="dash-chart-badge blue">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
              Revenue
            </div>
          </div>
          <div className="dash-chart-body"><RevenueByFoodCourt /></div>
        </div>
      </div>

      {/* Row 3: Trends & Funnel */}
      <div className="dash-section-label">Trends & Growth</div>
      <div className="dash-charts">
        <div className="dash-chart-card">
          <div className="dash-chart-header">
            <div>
              <h3 className="dash-chart-title">Avg Order Value</h3>
              <p className="dash-chart-desc">Basket size trend (7 days)</p>
            </div>
            <div className="dash-chart-badge green">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
              Value
            </div>
          </div>
          <div className="dash-chart-body"><AvgOrderValueTrend /></div>
        </div>
        <div className="dash-chart-card">
          <div className="dash-chart-header">
            <div>
              <h3 className="dash-chart-title">Registration Funnel</h3>
              <p className="dash-chart-desc">Food court onboarding pipeline</p>
            </div>
            <div className="dash-chart-badge blue">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg>
              Funnel
            </div>
          </div>
          <div className="dash-chart-body"><RegistrationFunnel /></div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
